import React from "react";
import { Spinner } from "./spinner";
import { cn } from "../../lib/utils";

const sizes = [
  {
    tiny: "px-1.5 h-6 text-sm",
    small: "px-1.5 h-8 text-sm",
    medium: "px-2.5 h-10 text-sm",
    large: "px-3.5 h-12 text-base"
  },
  {
    tiny: "w-6 h-6 text-sm",
    small: "w-8 h-8 text-sm",
    medium: "w-10 h-10 text-sm",
    large: "w-12 h-12 text-base"
  }
];

const types = {
  primary: "bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] hover:from-[#8a2be2] hover:to-[#c780ff] text-white fill-white shadow-lg hover:shadow-xl hover:shadow-[#8a2be2]/30",
  secondary: "bg-white dark:bg-[#1a0f2b] hover:bg-[#00000014] dark:hover:bg-[#ffffff17] text-[#6a0dad] dark:text-[#c780ff] fill-[#6a0dad] dark:fill-[#c780ff] border border-[#6a0dad]/30 dark:border-[#8a2be2]/30",
  tertiary: "bg-transparent dark:bg-transparent hover:bg-[#00000014] dark:hover:bg-[#ffffff17] text-[#6a0dad] dark:text-[#c780ff] fill-[#6a0dad] dark:fill-[#c780ff]",
  error: "bg-[#ea001d] dark:bg-[#e2162a] hover:bg-[#ae292f] dark:hover:bg-[#ff565f] text-white fill-white",
  warning: "bg-[#ff9300] hover:bg-[#d27504] text-[#0a0a0a] fill-[#0a0a0a]"
};

const shapes = {
  square: {
    tiny: "rounded",
    small: "rounded-md",
    medium: "rounded-md",
    large: "rounded-lg"
  },
  circle: {
    tiny: "rounded-[100%]",
    small: "rounded-[100%]",
    medium: "rounded-[100%]",
    large: "rounded-[100%]"
  },
  rounded: {
    tiny: "rounded-[100px]",
    small: "rounded-[100px]",
    medium: "rounded-[100px]",
    large: "rounded-[100px]"
  }
};

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  size?: keyof typeof sizes[0];
  type?: keyof typeof types;
  shape?: keyof typeof shapes;
  svgOnly?: boolean;
  children?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  shadow?: boolean;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  buttonType?: 'button' | 'submit' | 'reset';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  size = "medium",
  type = "primary",
  shape = "square",
  svgOnly = false,
  children,
  prefix,
  suffix,
  shadow = false,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  className,
  buttonType = "button",
  ...rest
}, ref) => {
  return (
    <button
      ref={ref}
      type={buttonType}
      disabled={disabled || loading}
      onClick={onClick}
      tabIndex={0}
      className={cn(
        "flex justify-center items-center gap-0.5 transition-all duration-100",
        sizes[+svgOnly][size],
        (disabled || loading) 
          ? "bg-[#f2f2f2] dark:bg-[#1a1a1a] text-[#8f8f8f] fill-[#8f8f8f] border border-[#ebebeb] dark:border-[#2e2e2e] cursor-not-allowed" 
          : types[type],
        shapes[shape][size],
        shadow ? " shadow-[0_0_0_1px_#00000014,_0px_2px_2px_#0000000a] border-none" : "",
        fullWidth ? " w-full" : "",
        "focus:outline-none focus:ring-2 focus:ring-[#8a2be2] focus:ring-offset-2",
        className
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === "large" ? 24 : 16} />
      ) : prefix}
      {children && (
        <span className={cn(
          "overflow-hidden whitespace-nowrap overflow-ellipsis font-sans",
          size === "tiny" ? "" : " px-1.5"
        )}>
          {children}
        </span>
      )}
      {!loading && suffix}
    </button>
  );
});

Button.displayName = "Button";

